import type { EntityType, Status, DataStatus } from '@/types/kyc';
import { KytAlertMetricType, KytAlertTemplate, type KytPolicyAlert } from '@/types/kyt-config';

export const toTitleCase = (key: string | null | undefined): string => String(key || '')
  .replace(/_/g, ' ')
  .replace(/\b\w/g, (m) => m.toUpperCase());

// Retained for existing policies, but unavailable when creating a new policy.
export const LEGACY_RISK_CATEGORY_SLUGS = new Set(['lawsuits']);

export const RISK_CATEGORY_LABELS_PT: Record<string, string> = {
  pep_titular: 'PEP titular',
  pep_related: 'PEP relacionado',
  economic_activity: 'Atividade econômica',
  location_city: 'Cidade de fronteira ou portuária',
  location_country: 'País - GAFI',
  age_person: 'Idade da pessoa',
  age_company: 'Idade da empresa',
  age_cpf: 'Idade da pessoa',
  age_cnpj: 'Idade da empresa',
  national_sanctions: 'Sanções nacionais',
  sanctions_national: 'Sanções nacionais',
  international_sanctions: 'Sanções internacionais',
  sanctions_international: 'Sanções internacionais',
  sanctioned_country: 'País sancionado',
  lawsuits: 'Processos Judiciais',
  lawsuits_criminal: 'Processos criminais',
  lawsuits_novo: 'Processos Judiciais',
  negative_media: 'Mídia Negativa',
  cnae_primary_main: 'CNAE principal',
  cnae_primary_main_cc_merchant: 'CNAE principal CC.M.',
  cnae_primary_branches: 'CNAE principal das filiais',
  cnae_primary_branches_cc_merchant: 'CNAE filiais CC.M.',
  dict: 'DICT',
  internal_restricted_list: 'Lista restritiva interna',
};

// Labels a category had before being renamed; instance names saved with them are still autogenerated.
const PREVIOUS_RISK_CATEGORY_LABELS_PT: Record<string, string[]> = {
  location_city: ['Cidade'],
  location_country: ['País'],
};

export const RISK_CATEGORY_ORDER = [
  'pep_titular',
  'pep_related',
  'economic_activity',
  'location_city',
  'location_country',
  'age_person',
  'age_company',
  'national_sanctions',
  'international_sanctions',
  'lawsuits',
  'negative_media',
];

export const RELATIONSHIP_LABELS_PT: Record<string, string> = {
  self: 'Própria entidade',
  shareholder_company: 'Pessoa Jurídica Acionista',
  shareholder_person: 'Acionista',
  legal_representative: 'Representante Legal',
  'shareholder company': 'Pessoa Jurídica Acionista',
  'shareholder person': 'Acionista',
  'legal representative': 'Representante Legal',
};

export const getRiskCategoryLabel = (
  categorySlug: string | null | undefined,
  fallbackLabel?: string | null,
  preserveCustomName = false,
): string => {
  const trimmedSlug = categorySlug?.trim();
  const translatedLabel = trimmedSlug && RISK_CATEGORY_LABELS_PT[trimmedSlug]
    ? RISK_CATEGORY_LABELS_PT[trimmedSlug]
    : fallbackLabel?.trim() || (trimmedSlug ? toTitleCase(trimmedSlug || '') : 'Verificação de risco');

  if (!preserveCustomName) {
    return translatedLabel;
  }

  const trimmedFallback = fallbackLabel?.trim();
  if (!trimmedFallback) return translatedLabel;

  const suffixMatch = trimmedFallback.match(/\s+#(\d+)$/);
  const suffix = suffixMatch ? ` #${suffixMatch[1]}` : '';
  const baseLabel = suffixMatch ? trimmedFallback.slice(0, suffixMatch.index).trim() : trimmedFallback;

  const normalizeDisplayText = (value: string | null | undefined): string =>
    String(value || '')
      .trim()
      .toLocaleLowerCase('pt-BR')
      .replace(/\s+/g, ' ');

  // Older autogenerated names were derived from the slug, for example
  // `negative_media` -> `Negative Media` or `negative media #2`.
  // When we detect that pattern, we swap in the central translated label.
  const generatedNames = new Set([
    normalizeDisplayText(categorySlug),
    normalizeDisplayText(toTitleCase(categorySlug || '')),
    normalizeDisplayText(String(categorySlug || '').replace(/_/g, ' ')),
    normalizeDisplayText(translatedLabel),
    ...(PREVIOUS_RISK_CATEGORY_LABELS_PT[trimmedSlug ?? ''] ?? []).map(normalizeDisplayText),
  ]);

  if (generatedNames.has(normalizeDisplayText(baseLabel))) {
    return `${translatedLabel}${suffix}`;
  }

  return trimmedFallback;
};

export const getRelationshipLabel = (relationship: string | null | undefined): string => {
  const trimmedRelationship = relationship?.trim();
  if (!trimmedRelationship) return '—';

  const normalizedRelationshipKey = trimmedRelationship
    .toLocaleLowerCase('pt-BR')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');

  if (RELATIONSHIP_LABELS_PT[trimmedRelationship]) {
    return RELATIONSHIP_LABELS_PT[trimmedRelationship];
  }

  if (RELATIONSHIP_LABELS_PT[normalizedRelationshipKey]) {
    return RELATIONSHIP_LABELS_PT[normalizedRelationshipKey];
  }

  return toTitleCase(trimmedRelationship || '');
};

export const getRelationshipIcon = (relationship: string | null | undefined): string => {
  const key = relationship?.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ');
  switch (key) {
    case 'self': return 'business_center';
    case 'shareholder company': return 'apartment';
    case 'shareholder person': return 'person';
    case 'legal representative': return 'badge';
    default: return 'hub';
  }
};

export const getEntityTypeIcon = (type: EntityType | null): string => {
    if (type === null) return 'diversity_3';
    return type === 'company' ? 'apartment' : 'person';
};

/** Narrow an untyped value (e.g. from a BigQuery payload) to `EntityType`. */
export const narrowToEntityType = (value: unknown): EntityType | undefined => {
    if (value === 'company') return 'company';
    if (value === 'person') return 'person';
    return undefined;
};

/** Icon for a monitoring policy instance scope (supports multiple entity types; list hub uses Material icon names). */
export const getMonitoringInstanceTypeIcon = (types: EntityType[]): string => {
    if (types.length >= 2) {
        return 'diversity_3';
    }
    if (types.length === 0) {
        return 'diversity_3';
    }
    return getEntityTypeIcon(types[0] ?? null);
};
export const getEntityTypeTranslation = (type: EntityType): string => (type === 'company' ? 'empresa' : 'pessoa');

export const getStatusClass = (status: Status): string => {
  switch (status) {
    case 'passed': return 'text-green-600';
    case 'failed': return 'text-red-600';
    case 'pending':
    case 'missing': return 'text-yellow-600';
    default: return 'text-gray-500';
  }
};

export const getStatusIconSmall = (status: Status): string => {
  switch (status) {
    case 'passed': return 'check_circle';
    case 'failed': return 'cancel';
    case 'pending':
    case 'missing': return 'help';
    default: return 'help';
  }
};

export const getNaIconSmall = (): string => 'do_not_disturb_on';

export const getFreshnessDotClass = (dataStatus: DataStatus): string => (dataStatus === 'updated' ? 'bg-green-500' : 'bg-red-500');

export const getRelationshipSeverity = (entity: { is_main_company: boolean }): 'info' | 'secondary' => (entity.is_main_company ? 'info' : 'secondary');

export const getStatusIcon = (status: Status): string => {
  switch (status) {
    case 'passed': return 'check_circle';
    case 'failed': return 'cancel';
    case 'missing': return 'help';
    default: return 'help';
  }
};

export const getStatusText = (status: Status): string => {
  switch (status) {
    case 'passed': return 'Verificado';
    case 'failed': return 'Falhou';
    case 'missing': return 'Dados ausentes';
    default: return 'Desconhecido';
  }
};

export const getStatusDescription = (status: Status): string => {
  switch (status) {
    case 'passed': return 'Sem problemas encontrados';
    case 'failed': return 'Problemas identificados';
    case 'missing': return 'Dados não disponíveis';
    default: return 'Status desconhecido';
  }
};

export const ORIGINAL_HEADER_BADGE: Record<string, string> = {
  age_cpf: 'PF',
  age_cnpj: 'PJ',
  cnae_primary_branches: 'BR',
  cnae_primary_branches_cc_merchant: 'CC.M.',
  cnae_primary_main: 'MAIN',
  cnae_primary_main_cc_merchant: 'CC.M.',
  lvl1_pep: 'L1',
  lvl2_pep: 'L2',
  border_city_cpf: 'PF',
  border_city_cnpj: 'PJ',
  sanctions_national: 'Nat',
  sanctions_international: 'Int',
};

export const getOriginalHeaderBadge = (key: string): string | null => ORIGINAL_HEADER_BADGE[key] || null;

export const getDaysSinceUpdate = (lastUpdated: string | Date | null | undefined): number => {
  if (!lastUpdated) return 0;
  const lastUpdate = new Date(lastUpdated);
  if (isNaN(lastUpdate.getTime())) return 0;
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - lastUpdate.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

export const CATEGORY_ICON_MAP: Record<string, string> = {
  pep_titular: 'person',
  pep_related: 'groups',
  economic_activity: 'insights',
  location_city: 'location_city',
  location_country: 'location_on',
  age_person: 'elderly',
  age_company: 'business',
  national_sanctions: 'flag',
  sanctions_national: 'flag',
  international_sanctions: 'public',
  sanctions_international: 'public',
  lawsuits: 'gavel',
  lawsuits_novo: 'gavel',
  negative_media: 'newspaper',
};

export const getCategoryIcon = (category: string): string => CATEGORY_ICON_MAP[category] || 'policy';

// Risk Level utilities
export type RiskLevelSlug = 'no_risk' | 'very_low' | 'low' | 'medium' | 'high' | 'very_high';

export interface RiskLevelDisplay {
  slug: RiskLevelSlug | null;
  label: string;
  icon: string;
  colorClass: string;
  bgClass: string;
  fillClass: string;
  borderClass: string;
  hexColor: string;
  hexBgColor: string;
  levelValue: number | null;
}

interface RiskLevelDisplayOptions {
  isMissing?: boolean;
}

export const RISK_LEVEL_CONFIG: Record<RiskLevelSlug | 'null', Omit<RiskLevelDisplay, 'slug' | 'levelValue'>> = {
  null: { label: 'Ausente', icon: 'help_outline', colorClass: 'text-gray-500', bgClass: 'bg-gray-50', fillClass: 'bg-gray-500', borderClass: 'border-gray-200', hexColor: '#6b7280', hexBgColor: '#f9fafb' },
  no_risk: { label: 'Sem Risco', icon: 'verified_user', colorClass: 'text-green-600', bgClass: 'bg-green-50', fillClass: 'bg-green-600', borderClass: 'border-green-200', hexColor: '#16a34a', hexBgColor: '#f0fdf4' },
  very_low: { label: 'Muito Baixo', icon: 'shield', colorClass: 'text-emerald-600', bgClass: 'bg-emerald-50', fillClass: 'bg-emerald-600', borderClass: 'border-emerald-200', hexColor: '#059669', hexBgColor: '#ecfdf5' },
  low: { label: 'Baixo', icon: 'gpp_good', colorClass: 'text-green-600', bgClass: 'bg-green-50', fillClass: 'bg-green-600', borderClass: 'border-green-200', hexColor: '#16a34a', hexBgColor: '#f0fdf4' },
  medium: { label: 'Médio', icon: 'gpp_bad', colorClass: 'text-amber-600', bgClass: 'bg-amber-50', fillClass: 'bg-amber-600', borderClass: 'border-amber-200', hexColor: '#d97706', hexBgColor: '#fffbeb' },
  high: { label: 'Alto', icon: 'gpp_bad', colorClass: 'text-orange-600', bgClass: 'bg-orange-50', fillClass: 'bg-orange-600', borderClass: 'border-orange-200', hexColor: '#ea580c', hexBgColor: '#fff7ed' },
  very_high: { label: 'Muito Alto', icon: 'gpp_bad', colorClass: 'text-red-600', bgClass: 'bg-red-50', fillClass: 'bg-red-600', borderClass: 'border-red-200', hexColor: '#dc2626', hexBgColor: '#fef2f2' }
};

/**
 * Risk Value to Slug Mapping Configuration
 * =========================================
 * This configuration controls how numeric risk values (0-5) map to risk level slugs.
 *
 * How it works:
 * - Thresholds are checked from top to bottom (highest minValue first)
 * - The first threshold where levelValue >= minValue determines the slug
 *
 * Current mapping:
 * - 5    → 'very_high'
 * - 4    → 'high'
 * - 1-3  → 'medium'
 * - 0    → 'low'
 *
 * To modify: Simply adjust minValue boundaries or change the slug assignments.
 * Example: To make 2-3 'medium' and 0-1 'low', change the medium threshold to minValue: 2
 */
export interface RiskValueThreshold {
  minValue: number;
  slug: RiskLevelSlug;
}

export const RISK_VALUE_THRESHOLDS: RiskValueThreshold[] = [
  { minValue: 5, slug: 'very_high' },
  { minValue: 4, slug: 'high' },
  { minValue: 1, slug: 'medium' },
  { minValue: 0, slug: 'low' },
];

/** Default slug when value is null/undefined */
export const DEFAULT_RISK_SLUG: RiskLevelSlug = 'low';

const VALID_RISK_LEVEL_SLUGS = new Set<RiskLevelSlug>(['low', 'medium', 'high', 'very_high']);

export const normalizeRiskLevelSlug = (level: string | null | undefined): RiskLevelSlug | null => {
  if (!level) return null;

  const normalizedLevel = level as RiskLevelSlug;
  return VALID_RISK_LEVEL_SLUGS.has(normalizedLevel) ? normalizedLevel : null;
};

export const getRiskLevelDisplay = (
  levelValue: number | null | undefined,
  options: RiskLevelDisplayOptions = {}
): RiskLevelDisplay => {
  if (options.isMissing) {
    return {
      slug: null,
      ...RISK_LEVEL_CONFIG.null,
      levelValue: null,
    };
  }

  if (levelValue === null || levelValue === undefined) {
    return { slug: null, ...RISK_LEVEL_CONFIG.null, levelValue: null };
  }

  let slug: RiskLevelSlug = DEFAULT_RISK_SLUG;
  for (const threshold of RISK_VALUE_THRESHOLDS) {
    if (levelValue >= threshold.minValue) {
      slug = threshold.slug;
      break;
    }
  }

  const config = RISK_LEVEL_CONFIG[slug] || RISK_LEVEL_CONFIG.low;
  return { slug, ...config, levelValue };
};

export const getHighestRiskLevelValue = (categories: { level_value: number | null; is_relevant: boolean }[]): number => {
  return categories
    .filter(c => c.is_relevant !== false)
    .reduce((max, c) => Math.max(max, c.level_value ?? 0), 0);
};

// KYT alert threshold display
export interface KytAlertThresholdDisplay {
  label: string;
  value: string;
  severity: 'danger' | 'warn' | 'success';
}

/** Formats a KYT alert's threshold (score points or risk level) for display. */
export const formatKytAlertThreshold = (alert: KytPolicyAlert): KytAlertThresholdDisplay => {
  const { metric_type, value } = alert.config_data_json;

  if (metric_type === KytAlertMetricType.SCORE) {
    return {
      label: 'Pontuação mínima',
      value: `${value} pts`,
      severity: 'warn',
    };
  }

  const display = getRiskLevelDisplay(value);
  return {
    label: 'Nível de risco mínimo',
    value: display.label,
    severity: 'warn',
  };
};

/** Maps each indicator `code` to the single-indicator alert that references it. */
export const buildIndicatorAlertMap = (alerts: KytPolicyAlert[] = []): Map<string, KytPolicyAlert> => {
  const map = new Map<string, KytPolicyAlert>();
  alerts
    .filter(a => a.template === KytAlertTemplate.SIMPLE_SINGLE_INDICATOR)
    .forEach(a => {
      const code = a.config_data_json?.indicator_code;
      if (code) map.set(code, a);
    });
  return map;
};

/** Whether the policy has its (single) global-score alert configured. */
export const hasGlobalAlert = (alerts: KytPolicyAlert[] = []): boolean =>
  alerts.some(a => a.template === KytAlertTemplate.GLOBAL);
